import React from 'react';
import { SectionWrapper } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import {
  DateToShortOrLongReadable,
  FormatAsCurrencyForLocale
} from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import PropTypes from 'prop-types';
import { PER_FREQUENCY_LABELS, FREQUENCY_CODE_ANNUALLY } from '../../../constants/constants';

import Loading from '../../shared/Loading/Loading';

import { getClaimingExample } from './summary';
import './renewalSummary.scss';
import { RenewalDataPropType } from '../../../propTypes/renewalData';
import { ERRORS } from '../consts';
import { toSentenceCasing } from '../../../utils/formatString';

export const RenewalSummary = ({ renewalData, isLoading, isError }) => {
  if (isLoading || isError || !renewalData)
    return (
      <SectionWrapper
        heading="Renewal summary"
        customCssClass="renewalSummary"
        isFixed
      >
        {(isError || !renewalData) && <p>{ERRORS.loadingError}</p>}
        {isLoading && <Loading />}
      </SectionWrapper>
    );

  const {
    PetName,
    FullPolicyNumber,
    RenewalStartDate,
    RenewalEndDate,
    LevelOfCover,
    BenefitPercentage,
    Excess,
    ExcessType,
    RoutineCare,
    BoosterCare,
    BoosterCareName,
    PaymentFrequency,
    PaymentAmount,
    PremiumAnnual,
    AnnualBenefitLimit
  } = renewalData;
  const excessTypeLabel = (ExcessType || 'annual excess').toLowerCase();

  const { billAmount, afterBenefitPercentage, afterExess, wePay, youPay } = getClaimingExample(renewalData);

  return (
    <SectionWrapper
      heading="Renewal summary"
      customCssClass="renewalSummary"
      isFixed
    >
      <ul className="renewalSummaryList">
        <li>
          <span>Policy no</span>
          <strong>{FullPolicyNumber}</strong>
        </li>
        <li>
          <span>Level of cover</span>
          <strong>{LevelOfCover}</strong>
        </li>
        <li>
          <span>Policy period</span>
          <strong>
            {`${DateToShortOrLongReadable(RenewalStartDate)} – ${DateToShortOrLongReadable(RenewalEndDate)}`}
          </strong>
        </li>
        <li>
          <span>Annual benefit limit</span>
          <strong>{FormatAsCurrencyForLocale(AnnualBenefitLimit ?? 0, { trimZeroDecimals: true })}</strong>
        </li>
        <li>
          <span>Benefit percentage</span>
          <strong>{`${BenefitPercentage}%`}</strong>
        </li>
        <li>
          <span>{toSentenceCasing(ExcessType) || 'Excess'}</span>
          <strong>{FormatAsCurrencyForLocale(Excess, { trimZeroDecimals: true })}</strong>
        </li>
        <li>
          <span>Routine Care</span>
          <strong>{RoutineCare ? 'Yes' : 'No'}</strong>
        </li>
        <li>
          <span>{BoosterCareName || 'Booster Care'}</span>
          <strong>{BoosterCare ? 'Yes' : 'No'}</strong>
        </li>
        <li>
          <span>Annual premium</span>
          <strong>{FormatAsCurrencyForLocale(PremiumAnnual)}</strong>
        </li>
        {PaymentFrequency?.toLowerCase() !== FREQUENCY_CODE_ANNUALLY.toLowerCase() && (
          <li>
            <span>Payment per {PER_FREQUENCY_LABELS[PaymentFrequency]?.toLowerCase()}</span>
            <strong>{FormatAsCurrencyForLocale(PaymentAmount)}</strong>
          </li>
        )}
        <li>
          <SectionWrapper
            heading="Claiming example"
            customCssClass="claimingExample"
          >
            <ul>
              <li>
                <span>
                  <strong>{`${PetName}’s vet bill is ${FormatAsCurrencyForLocale(billAmount, {
                    trimZeroDecimals: true
                  })}`}</strong>
                </span>
                <span />
              </li>
              <li>
                <span>{`Apply ${BenefitPercentage}% benefit percentage:`}</span>
                <span>{FormatAsCurrencyForLocale(afterBenefitPercentage, { trimZeroDecimals: true })}</span>
              </li>
              <li>
                <span>{`Subtract ${FormatAsCurrencyForLocale(Excess, {
                  trimZeroDecimals: true
                })} ${excessTypeLabel}:`}</span>
                <span>{FormatAsCurrencyForLocale(afterExess, { trimZeroDecimals: true })}</span>
              </li>
              <li>
                <strong>We pay:</strong>
                <strong>{FormatAsCurrencyForLocale(wePay, { trimZeroDecimals: true })}</strong>
              </li>
              <li>
                <strong>You pay:</strong>
                <strong>{FormatAsCurrencyForLocale(youPay, { trimZeroDecimals: true })}</strong>
              </li>
            </ul>
          </SectionWrapper>
        </li>
      </ul>
    </SectionWrapper>
  );
};

RenewalSummary.propTypes = {
  renewalData: RenewalDataPropType,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool
};
